'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { HUB_SUBTABS } from '../_lib/capabilities';
import { useHubData } from '../_lib/use-hub-data';
import type { GrowthKpis, PriorityItem, ThingBuilt } from '@/lib/hub/types';

/**
 * Hub Today / Growth / Project subtabs with ambient badges:
 *   Today   → count of items waiting for your OK (priority-stack)
 *   Growth  → revenue delta text (kpis)
 *   Project → count of things Hunter built
 */
export function HubSubtabs() {
  const params = useSearchParams();
  const view = params?.get('view') ?? 'today';

  const stack = useHubData<PriorityItem[]>('/api/hub/priority-stack');
  const kpis = useHubData<GrowthKpis>('/api/growth/kpis');
  const built = useHubData<ThingBuilt[]>('/api/project/things-built');

  const todayCount = stack.data?.length ?? 0;
  const growthDelta = kpis.data?.revenue.delta ?? null;
  const builtCount = built.data?.length ?? 0;

  const badgeFor = (id: string) => {
    if (id === 'today' && todayCount > 0) {
      return <span className="hub-tab-badge" title={`${todayCount} need you`}>{todayCount}</span>;
    }
    if (id === 'growth' && growthDelta && growthDelta.startsWith('+')) {
      return <span className="hub-tab-badge text up">{growthDelta.split(' ')[0]}</span>;
    }
    if (id === 'project' && builtCount > 0) {
      return <span className="hub-tab-badge" title={`${builtCount} things built`}>{builtCount}</span>;
    }
    return null;
  };

  return (
    <div className="hub-subtabs-divider">
      <nav className="hub-subtabs" aria-label="Hub views" role="tablist">
        {HUB_SUBTABS.map((tab) => {
          const active = tab.id === view;
          return (
            <Link
              key={tab.id}
              href={tab.href}
              role="tab"
              aria-selected={active}
              className={`hub-subtab${active ? ' active' : ''}`}
            >
              {tab.label}
              {badgeFor(tab.id)}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
